import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomDeviceModel } from 'utils/globalModels';
import { MessageErrorModel, useMessage } from 'hooks/useMessage';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { DEFAULT_DELAY_SUCCESS_MESSAGE } from 'constants/time';
import { UpdateSensorsBatch } from 'core/domain/sensors/repositories/updateSensorsBatch';
import { UpdateSensorsBatchItemModel, UpdateSensorsBatchResponseItemModel } from 'core/domain/sensors/models/sensorEntityModels';

interface FailedUpdatesModalLiterals {
  text: string;
  title: string;
  mainButtonText: string;
}

interface UpdateSensorsOptionsModel {
  failedUpdatesList: string[];
  failedUpdatesModalLiterals: FailedUpdatesModalLiterals;
  onToggleDeviceVisibility: (checked: boolean, deviceUuid: string, devices: CustomDeviceModel[]) => void;
  onToggleSensorVisibility: (checked: boolean, sensorUuid: string) => void;
  getIsDeviceVisibilityToggleChecked: (device: CustomDeviceModel) => boolean;
  getIsDeviceVisibilityToggleDisabled: (device: CustomDeviceModel) => boolean;
  getIsSensorVisibilityToggleDisabled: (sensorUuid: string, devices: CustomDeviceModel[]) => boolean;
  getIsVisibilityToggleLoading: (deviceOrSensorUuid: string) => boolean;
  onCloseFailedUpdatesModal: () => void;
  resetVisibleToggleLoading: () => void;
  isFailedUpdatesModalVisible: boolean;
}

export const useUpdateSensors = () => {
  const { t } = useTranslation();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const { setMessageError, setMessageSuccess } = useMessage();
  const [failedUpdatesList, setFailedUpdatesList] = useState<string[]>([]);
  const [successUpdatingSensors, setSuccessUpdatingSensors] = useState<boolean>(false);
  const [visibleToggleLoading, setVisibleToggleLoading] = useState<Record<string, boolean>>({});
  const [isFailedUpdatesModalVisible, setIsFailedUpdatesModalVisible] = useState<boolean>(false);

  const failedUpdatesModalLiterals: FailedUpdatesModalLiterals = {
    mainButtonText: t('close'),
    text: t('_ASSET_DEVICE_FAILED_SENSORS_UPDATES_MODAL_TEXT'),
    title: t('_ASSET_DEVICE_FAILED_SENSORS_UPDATES_MODAL_TITLE'),
  };

  const onCloseFailedUpdatesModal = () => {
    setIsFailedUpdatesModalVisible(false);
  };

  const getVisibleToggleLoadingEntries = (keys: string[], value: boolean): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    keys.forEach((key) => {
      result[key] = value;
    });

    return result;
  };

  const resetVisibleToggleLoading = () => {
    setVisibleToggleLoading({});
  };

  const updateVisibleToggleLoading = (keys: string[], value: boolean) => {
    setVisibleToggleLoading((prev) => ({
      ...prev,
      ...getVisibleToggleLoadingEntries(keys, value),
    }));
  };

  const getIsVisibilityToggleLoading = (deviceOrSensorUuid: string): boolean => {
    return visibleToggleLoading[deviceOrSensorUuid] ?? false;
  };

  const getDeviceVisibleToggleKeys = (deviceUuid: string, devices: CustomDeviceModel[]): string[] => {
    const device = devices.find((device) => device.uuid === deviceUuid);
    const sensorUuids = device?.sensors.map((sensor) => sensor.accessSensorId) || [];

    return [deviceUuid, ...sensorUuids];
  };

  const parseFailedSensorUpdates = (sensorsList: UpdateSensorsBatchResponseItemModel[]): string[] => {
    return sensorsList.map((sensor) => {
      const { name, room, sensorType, sensorId } = sensor;
      return t('_ASSET_DEVICE_FAILED_SENSORS_UPDATES_MODAL_LIST_ITEM_TEXT', { name, room, type: sensorType, id: sensorId });
    });
  };

  const showFailedUpdatesModal = (failedUpdates: UpdateSensorsBatchResponseItemModel[]) => {
    setFailedUpdatesList(parseFailedSensorUpdates(failedUpdates));
    setIsFailedUpdatesModalVisible(true);
  };

  const handleFailedUpdates = (failedUpdates: UpdateSensorsBatchResponseItemModel[], sensorUuidList: string[]) => {
    failedUpdates.length
      ? showFailedUpdatesModal(failedUpdates)
      : setMessageSuccess({
          description: sensorUuidList.length > 1 ? t('_UPDATE_SENSORS_SUCCESS_MESSAGE') : t('_UPDATE_SENSOR_SUCCESS_MESSAGE'),
        });
  };

  const getErrorDescription = (error: MessageErrorModel, sensorUuidList: string[]): string => {
    if (error.code === 504) {
      return t('_UPDATE_SENSOR_GATEWAY_OFFLINE_ERROR_MESSAGE');
    }

    return sensorUuidList.length > 1 ? t('_UPDATE_SENSORS_ERROR_MESSAGE') : t('_UPDATE_SENSOR_ERROR_MESSAGE');
  };

  const updateSensors = async (checked: boolean, sensorUuidList: string[]) => {
    try {
      const sensorsData: UpdateSensorsBatchItemModel[] = sensorUuidList.map((sensorUuid) => {
        return {
          id: sensorUuid,
          visible: checked,
        };
      });
      const failedUpdates = await UpdateSensorsBatch({ host, token, sensorsData });

      handleFailedUpdates(failedUpdates, sensorUuidList);
      setSuccessUpdatingSensors(true);
    } catch (error) {
      resetVisibleToggleLoading();
      const description = getErrorDescription(error as MessageErrorModel, sensorUuidList);
      setMessageError({ description });
    } finally {
      setTimeout(() => {
        setSuccessUpdatingSensors(false);
      }, DEFAULT_DELAY_SUCCESS_MESSAGE);
    }
  };

  const onToggleDeviceVisibility = (checked: boolean, deviceUuid: string, devices: CustomDeviceModel[]) => {
    const keysToUpdate = getDeviceVisibleToggleKeys(deviceUuid, devices);
    updateVisibleToggleLoading(keysToUpdate, true);

    const device = devices.find((device) => device.uuid === deviceUuid);
    const sensorUuidList = device?.sensors.map((sensor) => sensor.accessSensorId) ?? [];

    updateSensors(checked, sensorUuidList);
  };

  const onToggleSensorVisibility = (checked: boolean, sensorUuid: string) => {
    const keysToUpdate = [sensorUuid];
    updateVisibleToggleLoading(keysToUpdate, true);

    updateSensors(checked, keysToUpdate);
  };

  const getIsDeviceVisibilityToggleChecked = (device: CustomDeviceModel): boolean => {
    return device.sensors.some((sensor) => sensor.visible);
  };

  const getIsDeviceVisibilityToggleDisabled = (device: CustomDeviceModel): boolean => {
    return !!device.shared || device.gatewayUuid === null;
  };

  const getIsSensorVisibilityToggleDisabled = (sensorUuid: string, devices: CustomDeviceModel[]): boolean => {
    const device = devices.find((device) => device.sensors.some((sensor) => sensor.accessSensorId === sensorUuid));

    return !!device?.shared || device?.gatewayUuid === null;
  };

  const updateSensorsOptions: UpdateSensorsOptionsModel = {
    failedUpdatesList,
    failedUpdatesModalLiterals,
    onToggleDeviceVisibility,
    onToggleSensorVisibility,
    getIsDeviceVisibilityToggleChecked,
    getIsDeviceVisibilityToggleDisabled,
    getIsSensorVisibilityToggleDisabled,
    getIsVisibilityToggleLoading,
    onCloseFailedUpdatesModal,
    resetVisibleToggleLoading,
    isFailedUpdatesModalVisible,
  };

  return {
    updateSensorsOptions,
    successUpdatingSensors,
  };
};
